import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Tooltip, OverlayTrigger } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";

const emojiMap = {
  Satisfied: "\u{1F60A}",
  Neutral: "\u{1F610}",
  Dissatisfied: "\u{1F61E}",
};

const StorageStatus = ({
  data,
  className,
  progressBarPercentages = {},
  progressBarCounts = {},
}) => {
  const progressBarData = Object?.keys(progressBarPercentages)?.map((key) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    size: isNaN(Number(progressBarPercentages[key])) ? 0 : Number(progressBarPercentages[key]),
    count: isNaN(Number(progressBarCounts[key])) ? 0 : Number(progressBarCounts[key]),
  })) || [];

  // Check if there's no data available
  const hasNoData = progressBarData.length === 0 || progressBarData.every(item => item.count === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="h-100"
    >
      <Card
        className={`${className} border-0 h-100`}
        style={{
          borderRadius: "16px",
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
        }}
      >
        <Card.Header
          className="pb-2 pt-2 px-3 bg-transparent border-0"
          style={{ borderBottom: "1px solid rgba(226, 232, 240, 0.8)" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",
              }}
            >
              <FontAwesomeIcon
                icon={["fas", "heart"]}
                style={{ color: "#475569", fontSize: "16px" }}
              />
            </div>
            <div>
              <h5
                className="mb-1 fw-bold"
                style={{
                  fontSize: "0.95rem",
                  color: "#0f172a",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  lineHeight: "1.4",
                  letterSpacing: "-0.01em"
                }}
              >
                Sentiment Breakdown
              </h5>
              <span
                className="d-block"
                style={{
                  fontSize: "0.75rem",
                  color: "#64748b",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  lineHeight: "1.5",
                  fontWeight: "400"
                }}
              >
                Customer Feedback Analysis
              </span>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="pt-3 pb-3 px-3 d-flex flex-column" style={{ flexGrow: 1, justifyContent: "center" }}>
          {hasNoData ? (
            <div className="xsurvey-no-data">
              <div className="xsurvey-no-data-icon">
                <FontAwesomeIcon icon={["fas", "heart"]} />
              </div>
              <p className="xsurvey-no-data-text">No data available</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {progressBarData.map((item, index) => {
              const [width, setWidth] = useState(0);

              useEffect(() => {
                const timer = setTimeout(() => {
                  setWidth(item.size);
                }, 500 + (index * 100));
                return () => clearTimeout(timer);
              }, [item.size, index]);

              const getColors = () => {
                if (item.name === "Satisfied") {
                  return {
                    bg: "#f0fdf4",
                    border: "#22c55e",
                    text: "#16a34a",
                    bar: "#22c55e",
                    iconBg: "#dcfce7"
                  };
                }
                if (item.name === "Neutral") {
                  return {
                    bg: "#f8fafc",
                    border: "#94a3b8",
                    text: "#64748b",
                    bar: "#94a3b8",
                    iconBg: "#f1f5f9"
                  };
                }
                return {
                  bg: "#fef2f2",
                  border: "#ef4444",
                  text: "#dc2626",
                  bar: "#ef4444",
                  iconBg: "#fee2e2"
                };
              };

              const colors = getColors();

              return (
                <OverlayTrigger
                  key={index}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${index}`} className="custom-tooltip">
                      <div style={{ fontWeight: "bold", fontSize: "1rem" }}>
                        {item.size.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: "0.9rem" }}>
                        {item.name}: {item.count} responses
                      </div>
                    </Tooltip>
                  }
                >
                  <div style={{ cursor: "pointer" }}>
                    <div
                      className="d-flex align-items-center gap-2 p-2 rounded"
                      style={{
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}30`,
                        transition: "all 0.2s ease",
                        borderRadius: "12px",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = `0 4px 12px ${colors.border}20`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {/* Icon */}
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          backgroundColor: colors.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontSize: "22px" }}>
                          {emojiMap[item.name] || emojiMap.Neutral}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span
                            className="fw-semibold"
                            style={{
                              fontSize: "0.85rem",
                              color: "#1e293b",
                              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                              letterSpacing: "0.01em",
                              lineHeight: "1.5"
                            }}
                          >
                            {item.name}
                          </span>
                          <div className="d-flex align-items-baseline gap-2" style={{ flexShrink: 0 }}>
                            <span
                              className="fw-bold"
                              style={{
                                fontSize: "0.9rem",
                                color: colors.text,
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                lineHeight: "1.4",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {item.size.toFixed(1)}%
                            </span>
                            <span
                              style={{
                                fontSize: "0.8rem",
                                color: "#64748b",
                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                                fontWeight: "500",
                                whiteSpace: "nowrap",
                                lineHeight: "1.4"
                              }}
                            >
                              ({item.count})
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            height: "10px",
                            backgroundColor: "#f1f5f9",
                            borderRadius: "6px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${width}%`,
                              backgroundColor: colors.bar,
                              borderRadius: "6px",
                              transition: "width 1s ease-out",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </OverlayTrigger>
              );
              })}
            </div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

StorageStatus.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }).isRequired
  ),
  className: PropTypes.string,
  progressBarPercentages: PropTypes.object,
  progressBarCounts: PropTypes.object,
};

export default StorageStatus;
